import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/ui/button';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import ENV from 'src/env.json';

/**
 * Periodic sync check interval — checks for new SW version every hour.
 */
const SYNC_PERIOD = 60 * 60 * 1000;

/**
 * Grace period before the gentle banner becomes a blocking modal.
 * Gives users time to finish current work before forcing the update.
 */
const FORCE_UPDATE_DELAY = 2 * 60 * 1000; // 2 minutes

const registerPeriodicSync = (period: number, swUrl: string, r: ServiceWorkerRegistration) => {
  if (period <= 0) return;

  setInterval(async () => {
    if ('onLine' in navigator && !navigator.onLine) return;

    try {
      const resp = await fetch(swUrl, {
        cache: 'no-store',
        headers: {
          cache: 'no-store',
          'cache-control': 'no-cache',
        },
      });

      if (resp?.status === 200) await r.update();
    } catch (e) {
      console.error('cannot ping/update sw.js', e);
    }
  }, period);
};

/**
 * Clears all browser caches, unregisters service workers,
 * and force-reloads to ensure a completely fresh state.
 */
const forceHardRefresh = async () => {
  try {
    // 1. Delete all browser caches
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((name) => caches.delete(name)));

    // 2. Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }

    console.log('[AppUpdate] Cleared all caches and unregistered service workers');
  } catch (error) {
    console.error('[AppUpdate] Failed to clear caches:', error);
  }

  // 3. Hard reload — bypass any remaining cache
  window.location.reload();
};

const AppUpdateDialog = () => {
  const { t } = useTranslation();
  // QA testing: add ?force-update to URL on non-production environments
  const forceTest =
    ENV.NODE_ENV !== 'prod' &&
    new URLSearchParams(window.location.search).has('force-update');
  const [updateAvailable, setUpdateAvailable] = useState(forceTest);
  const [forceBlocking, setForceBlocking] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useRegisterSW({
    onNeedRefresh() {
      setUpdateAvailable(true);
    },
    onRegisteredSW(swUrl, r) {
      if (SYNC_PERIOD <= 0) return;
      if (r?.active?.state === 'activated') {
        registerPeriodicSync(SYNC_PERIOD, swUrl, r);
      } else if (r?.installing) {
        r.installing.addEventListener('statechange', (e) => {
          const sw = e.target as ServiceWorker;
          if (sw.state === 'activated') registerPeriodicSync(SYNC_PERIOD, swUrl, r);
        });
      }
    },
  });

  // Start the countdown to blocking modal when banner appears
  useEffect(() => {
    if (!updateAvailable) return;

    timerRef.current = setTimeout(() => {
      setForceBlocking(true);
    }, FORCE_UPDATE_DELAY);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [updateAvailable]);

  const handleUpdate = useCallback(async () => {
    setIsUpdating(true);
    await forceHardRefresh();
  }, []);

  // Phase 2: Blocking full-screen modal (after grace period)
  if (updateAvailable && forceBlocking) {
    return (
      <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black/60 tw-backdrop-blur-sm">
        <div className="tw-mx-4 tw-w-full tw-max-w-md tw-rounded-xl tw-bg-white tw-p-8 tw-text-center tw-shadow-2xl">
          <div className="tw-mx-auto tw-mb-4 tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-brand/10">
            <RefreshCw
              className={`tw-h-8 tw-w-8 tw-text-brand ${isUpdating ? 'tw-animate-spin' : ''}`}
            />
          </div>

          <h2 className="tw-mb-2 tw-text-xl tw-font-bold tw-text-gray-900">
            {t('Update Required')}
          </h2>

          <p className="tw-mb-6 tw-text-sm tw-text-gray-600">
            {t(
              'A new version of BeCreatives has been deployed. Please update now to continue using the app.',
            )}
          </p>

          <Button
            className="tw-w-full"
            size="lg"
            variant="brand"
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <RefreshCw className="tw-mr-2 tw-h-4 tw-w-4 tw-animate-spin" />
                {t('Updating...')}
              </>
            ) : (
              <>
                <RefreshCw className="tw-mr-2 tw-h-4 tw-w-4" />
                {t('Update & Reload')}
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Phase 1: Gentle top banner (first 2 minutes)
  if (updateAvailable) {
    return (
      <div className="tw-fixed tw-inset-x-0 tw-bottom-6 tw-z-[9999] tw-flex tw-justify-center tw-px-4">
        <div className="tw-flex tw-items-center tw-gap-4 tw-rounded-xl tw-bg-gray-900 tw-px-6 tw-py-3.5 tw-text-sm tw-text-white tw-shadow-2xl tw-ring-1 tw-ring-white/10">
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-brand/20">
              <RefreshCw
                className={`tw-h-4 tw-w-4 tw-text-brand ${isUpdating ? 'tw-animate-spin' : ''}`}
              />
            </div>

            <div>
              <p className="tw-font-semibold">{t('New update available')}</p>
              <p className="tw-text-xs tw-text-gray-400">
                {t('Refresh to get the latest features')}
              </p>
            </div>
          </div>

          <Button
            size="sm"
            variant="brand"
            className="tw-ml-2 tw-rounded-lg tw-px-4 tw-py-2 tw-text-sm tw-font-semibold"
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <RefreshCw className="tw-mr-1.5 tw-h-3.5 tw-w-3.5 tw-animate-spin" />
                {t('Updating...')}
              </>
            ) : (
              t('Update Now')
            )}
          </Button>
        </div>
      </div>
    );
  }

  return null;
};

export default AppUpdateDialog;
